import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Plus,
  FileSpreadsheet,
  Search,
  LayoutGrid,
  List,
  Mail,
  Phone,
  Pencil,
  Send,
  UserPlus,
  UserCheck,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePersons, useInvitePerson } from '@/hooks/use-persons';
import { handleApiError } from '@/lib/api-client';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { AddPersonDialog } from './add-person-dialog';
import { EditPersonDialog } from './edit-person-dialog';
import { CompanyRoleSelectItems } from '@/app/components/company-role-select-items';
import type { Person, UserRole } from '@/lib/types';

export function PeopleListPage({ basePath = '/people' }: { basePath?: string }) {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [view, setView] = useState<'grid' | 'table'>('table');
  const [addOpen, setAddOpen] = useState(false);
  const [editPersonId, setEditPersonId] = useState<number | null>(null);
  const [invitePerson, setInvitePerson] = useState<Person | null>(null);
  const [inviteRole, setInviteRole] = useState<UserRole>('User');
  const [adminPromotionOpen, setAdminPromotionOpen] = useState(false);
  const [adminPromotionPerson, setAdminPromotionPerson] = useState<Person | null>(null);
  const navigate = useNavigate();
  const inviteMutation = useInvitePerson();
  const { isModuleEnabled } = usePlatformModules();
  const crewEnabled = isModuleEnabled('crew');
  const { canCreatePeople, canCreateTechs } = usePermissions();
  const canManagePeople = canCreatePeople;
  const canMakeTech = crewEnabled && canCreateTechs;

  const { data, isLoading } = usePersons({ q: search || undefined });
  const persons = data?.results ?? [];

  const handleEditPerson = (person: Person) => {
    if (person.user_id) {
      navigate(`${basePath}/${person.user_id}/edit`);
    } else {
      setEditPersonId(person.id);
    }
  };

  const handleMakeTech = (person: Person) => {
    const params = new URLSearchParams({
      person_id: String(person.id),
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email ?? '',
      phone: person.phone ?? '',
      address: person.address ?? '',
      city: person.city ?? '',
      state_province: person.state_province ?? '',
      country: person.country ?? '',
      postal_code: person.postal_code ?? '',
    });
    navigate(`/crew/techs/new?${params.toString()}`);
  };

  const handleInvitePerson = (person: Person) => {
    setInviteRole('User');
    setInvitePerson(person);
  };

  const submitInvite = async (
    person: Person,
    role: UserRole,
    confirmAdminGrant = false,
  ) => {
    try {
      await inviteMutation.mutateAsync({
        id: person.id,
        data: {
          role,
          ...(confirmAdminGrant ? { confirm_admin_grant: true } : {}),
        },
      });
      toast.success(`Invitation sent to ${person.email}`);
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message);
    }
  };

  const handleConfirmInvite = async () => {
    if (!invitePerson) return;
    const person = invitePerson;
    setInvitePerson(null);
    if (inviteRole === 'Admin') {
      setAdminPromotionPerson(person);
      setAdminPromotionOpen(true);
      return;
    }
    await submitInvite(person, inviteRole);
  };

  const handleConfirmAdminPromotion = async () => {
    if (!adminPromotionPerson) return;
    const person = adminPromotionPerson;
    setAdminPromotionPerson(null);
    setAdminPromotionOpen(false);
    await submitInvite(person, 'Admin', true);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-7 w-7" />
            People
          </h1>
          <p className="text-gray-600 mt-1">
            {data?.count ?? 0} {(data?.count ?? 0) !== 1 ? 'people' : 'person'} in your organization
          </p>
        </div>
        {canManagePeople && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate(`${basePath}/import`)}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Person
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by name, email, or phone..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex border rounded-md">
          <Button
            variant={view === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setView('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'table' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setView('table')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-1" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && persons.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900">No people found</h3>
            <p className="text-gray-600 mt-1">
              {search
                ? 'Try adjusting your search.'
                : 'Add your first person to get started.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Grid View */}
      {!isLoading && persons.length > 0 && view === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {persons.map((person) => (
            <PersonCard
              key={person.id}
              person={person}
              canMakeTech={canMakeTech}
              canManagePeople={canManagePeople}
              onEdit={handleEditPerson}
              onMakeTech={handleMakeTech}
              onInvite={handleInvitePerson}
            />
          ))}
        </div>
      )}

      {/* Table View */}
      {!isLoading && persons.length > 0 && view === 'table' && (
        <PersonTable
          persons={persons}
          basePath={basePath}
          canMakeTech={canMakeTech}
          canManagePeople={canManagePeople}
          onEdit={handleEditPerson}
          onMakeTech={handleMakeTech}
          onInvite={handleInvitePerson}
        />
      )}

      <AlertDialog
        open={invitePerson !== null}
        onOpenChange={(open) => { if (!open) setInvitePerson(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invite to platform?</AlertDialogTitle>
            <AlertDialogDescription>
              {invitePerson?.email
                ? `Send an email invitation to ${invitePerson.first_name} ${invitePerson.last_name} (${invitePerson.email}) so they can set up their own account. Their existing record will be linked automatically when they accept.`
                : `${invitePerson?.first_name ?? ''} ${invitePerson?.last_name ?? ''} has no email address. Add an email to their record before inviting them.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {invitePerson?.email && (
            <div className="space-y-2">
              <Label htmlFor="invite-company-role">Company role</Label>
              <Select
                value={inviteRole}
                onValueChange={(value) => setInviteRole(value as UserRole)}
              >
                <SelectTrigger id="invite-company-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <CompanyRoleSelectItems />
                </SelectContent>
              </Select>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {invitePerson?.email && (
              <AlertDialogAction onClick={handleConfirmInvite}>
                Send Invitation
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={adminPromotionOpen} onOpenChange={setAdminPromotionOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote user to Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Admins have full access to company settings, users, and every enabled module. Promote{' '}
              {adminPromotionPerson?.full_name} only if they should have that access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setInvitePerson(adminPromotionPerson);
                setAdminPromotionPerson(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAdminPromotion}>
              Promote to Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddPersonDialog open={addOpen} onOpenChange={setAddOpen} />
      <EditPersonDialog
        personId={editPersonId}
        open={editPersonId !== null}
        onOpenChange={(open) => { if (!open) setEditPersonId(null); }}
      />
    </div>
  );
}

function PersonCard({
  person,
  canMakeTech,
  canManagePeople,
  onEdit,
  onMakeTech,
  onInvite,
}: {
  person: Person;
  canMakeTech: boolean;
  canManagePeople: boolean;
  onEdit: (p: Person) => void;
  onMakeTech: (p: Person) => void;
  onInvite: (p: Person) => void;
}) {
  const initials = `${(person.first_name || '?')[0]}${(person.last_name || '?')[0]}`.toUpperCase();

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-medium text-gray-900 truncate block">
              {person.first_name} {person.last_name}
            </span>
            <div className="flex flex-col gap-1 mt-2 text-xs text-gray-500">
              {person.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail className="h-3 w-3" /> {person.email}
                </span>
              )}
              {person.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {person.phone}
                </span>
              )}
              {(person.city || person.country) && (
                <span className="text-gray-400">
                  {[person.city, person.country].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 mt-2 flex-wrap">
              {canManagePeople && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => onEdit(person)}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              )}
              {canManagePeople && !person.user_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => onInvite(person)}
                >
                  <Send className="h-3 w-3 mr-1" />
                  Invite to platform
                </Button>
              )}
              {canMakeTech && (
                person.has_tech_profile ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                    <UserCheck className="h-3 w-3" />
                    Technician
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => onMakeTech(person)}
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Make Technician
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PersonTable({
  persons,
  basePath: _basePath,
  canMakeTech,
  canManagePeople,
  onEdit,
  onMakeTech,
  onInvite,
}: {
  persons: Person[];
  basePath: string;
  canMakeTech: boolean;
  canManagePeople: boolean;
  onEdit: (p: Person) => void;
  onMakeTech: (p: Person) => void;
  onInvite: (p: Person) => void;
}) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>City</TableHead>
            <TableHead>Country</TableHead>
            {canManagePeople && <TableHead></TableHead>}
            {canMakeTech && <TableHead></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {persons.map((person) => (
            <TableRow key={person.id}>
              <TableCell className="font-medium">
                {person.first_name} {person.last_name}
              </TableCell>
              <TableCell>{person.phone || '—'}</TableCell>
              <TableCell>{person.email || '—'}</TableCell>
              <TableCell>{person.city || '—'}</TableCell>
              <TableCell>{person.country || '—'}</TableCell>
              {canManagePeople && (
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(person)}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    {!person.user_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onInvite(person)}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Invite to platform
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
              {canMakeTech && (
                <TableCell>
                  {person.has_tech_profile ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                      <UserCheck className="h-3 w-3" />
                      Technician
                    </span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onMakeTech(person)}
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Make Technician
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
